import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { Flex, IconV3, useToasts } from "@rpi/openapi-core";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { AppPage } from "../../components/AppPage";
import { RpiPagination, RpiPaginationRow } from "../../components/RpiPagination";
import { RpiSearchFilter } from "../../components/search-filter/RpiSearchFilter";
import { orderColumns } from "../../configuration/columns/order.column";
import { useOrderFilters } from "../../configuration/filters/order.filter";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useRpiPagination } from "../../hooks/usePagination";
import { RpiPerPage } from "../../hooks/useRpiPerPage";
import { RpiTable } from "../../rpi-core/table/RpiTable";
import { SearchGeneral, SearchInputs, SearchSort } from "../../utils/graphql.util";
import { OrdersTableFooter, OrdersTableV2 } from "./OrdersTableV2";
import { Group, MantineProvider, Menu } from "@mantine/core";
import { PaginationV2 } from "../../components/PaginationV2";
import { rpaTheme } from "../../theme/rpaTheme";
import { featureFlags } from "../../featureFlags";
import { SearchInput, toSearchInputArray } from "../../configuration/filters/searchInput";
import { FilterChips } from "../../components/FilterChips";
import { orderSearchLabels, orderSearchRenderers } from "../../utils/searchField.util";
import { Button } from "@blurb/codex-react";

export const OrdersView: React.FC = () => {
  const user = useAuthUser();
  const [searchParams] = useSearchParams();
  const currentDate = new Date();
  const oneMonthBack: Date = new Date();
  oneMonthBack.setMonth(currentDate.getMonth() - 1);

  const initialOrderSearch =
    searchParams.get("attention") === "true"
      ? [
          {
            field: GraphQL.OrderSearchField.Status,
            condition: GraphQL.SearchCondition.In,
            values: [GraphQL.PublicOrderStatus.Exception, GraphQL.PublicOrderStatus.Failed],
          },
          {
            field: GraphQL.OrderSearchField.CreatedAt,
            condition: GraphQL.SearchCondition.Between,
            firstValue: oneMonthBack.toISOString(),
            secondValue: currentDate.toISOString(),
          },
        ]
      : [];

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.OrdersQueryVariables>>({
    by: GraphQL.OrderSearchField.CreatedAt,
    sortDirection: GraphQL.SortDirection.Desc,
  });
  const [searchInputs, setSearchInputs] =
    React.useState<SearchInputs<GraphQL.OrdersQueryVariables>>(initialOrderSearch);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.OrdersQueryVariables>>();

  const searchFilters = toSearchInputArray(searchInputs);
  const setSearchFilters = (
    recipe: (filters: SearchInput<GraphQL.OrderSearchField>[]) => SearchInput<GraphQL.OrderSearchField>[]
  ) => {
    setSearchInputs((prevSearchInputs) => {
      return recipe(toSearchInputArray(prevSearchInputs));
    });
  };

  const queryInputs = React.useMemo(() => {
    const sandboxFilter = {
      field: GraphQL.OrderSearchField.IsSandbox,
      condition: GraphQL.SearchCondition.Equals,
      firstValue: user.isSandbox.toString(),
    };
    return [sandboxFilter, ...(searchInputs || [])];
  }, [user.isSandbox, searchInputs]);

  const pagination = useRpiPagination({ perPage: featureFlags.newTableView ? 20 : 10 });

  const orderFilters = useOrderFilters();

  const { data, isLoading, error, refetch } = GraphQL.useOrdersQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: queryInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.orders.totalCount),
    }
  );
  const orders = React.useMemo(() => data?.orders.data, [data]);

  const { showSuccessToast, showErrorToast } = useToasts();

  const { mutate: sendSandboxPaymentNotification, isLoading: isSandboxPaymentLoading } =
    GraphQL.useSandboxPaymentNotificationMutation({
      onSuccess: (data) => {
        if (data.sandboxPaymentNotification.success) {
          showSuccessToast(
            "Payment success webhook sent! Please check your endpoint and inbox for the confirmation email."
          );
        } else {
          // success denotes whether or not a success event was sent and indicates a successful submission either way
          showSuccessToast("Payment failed webhook sent! Please check your endpoint.");
        }
      },
      onError: (error) => {
        if (isGraphQLError(error)) {
          showErrorToast(
            'Payment webhook test failed. Make sure you have orders in "Awaiting Payment" status and try again.'
          );
        } else {
          showErrorToast("Sandbox payment notification failed.");
        }
      },
    });

  return (
    <AppPage path={["Orders"]} onReload={refetch}>
      <AppPage.Section>
        <Flex.Row justify="space-between" align="flex-end">
          <RpiSearchFilter
            placeholder="Search by Order ID"
            filters={orderFilters}
            initialInputs={initialOrderSearch}
            dateRangeField={GraphQL.OrderSearchField.CreatedAt}
            onChange={setSearchInputs}
            onSearch={setSearchGeneral}
          />

          {(user.is.CUSTOMER_ADMIN || user.is.USER) && (
            <>
              {user.isSandbox && (
                <Menu width={240} position="bottom-end" withinPortal>
                  <Menu.Target>
                    <Button
                      variant="filled"
                      intent="primary"
                      size="large"
                      loading={isSandboxPaymentLoading}
                      aria-label={"Test Order Payments"}
                    >
                      <Group>
                        <IconV3 icon="credit_card" color="inherit" />
                        Test Order Payments
                      </Group>
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item onClick={() => sendSandboxPaymentNotification({ input: { success: true } })}>
                      Payment Success
                    </Menu.Item>
                    <Menu.Item onClick={() => sendSandboxPaymentNotification({ input: { success: false } })}>
                      Payment Failed
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </>
          )}
        </Flex.Row>
        {featureFlags.newTableView && (
          /** Keeping this in the original OrdersView so it can be tested while the new filters haven't been made yet  */
          <FilterChips
            filters={searchFilters}
            onSetFilters={setSearchFilters}
            filterLabels={orderSearchLabels}
            valueRenderers={orderSearchRenderers}
          />
        )}
        <>
          {featureFlags.newTableView ? (
            <MantineProvider theme={rpaTheme}>
              <OrdersTableV2
                isLoading={isLoading}
                error={error}
                orders={orders}
                withSortBy={{
                  defaultValue: {
                    by: GraphQL.OrderSearchField.CreatedAt,
                    sortDirection: GraphQL.SortDirection.Desc,
                  },
                  onChange: setSortInput,
                }}
              >
                <OrdersTableFooter>
                  <PaginationV2 pagination={pagination} />
                </OrdersTableFooter>
              </OrdersTableV2>
            </MantineProvider>
          ) : (
            <>
              <RpiTable
                isLoading={isLoading}
                error={error}
                perPage={pagination.perPage}
                data={orders}
                columns={orderColumns}
                emptyMessage={
                  searchInputs?.length || searchGeneral ? "No orders found" : "Your order history will appear here."
                }
                withSortBy={{
                  defaultValue: {
                    by: GraphQL.OrderSearchField.CreatedAt,
                    sortDirection: GraphQL.SortDirection.Desc,
                  },
                  onChange: setSortInput,
                }}
              />

              <RpiPaginationRow>
                <RpiPagination pagination={pagination} />
                <RpiPerPage perPage={pagination.perPage} setPerPage={pagination.setPerPage} />
              </RpiPaginationRow>
            </>
          )}
        </>
      </AppPage.Section>
    </AppPage>
  );
};
